
import { 
  IStringNumHash, 
  FloatingFeature
} from "@models";
import { AbilityScoreExpandedType } from "@dndModels/Types";


/**
 * These are spells that aren't on the regular 
 * spell list for the class, but through some 
 * feature (usually from a Subclass) 
 * they gain access to them.  
 * If they're learned automatically, then they're
 * always_known.
 */
export class BonusSpells extends FloatingFeature {
  spells: { spell_id: string, level: number }[] = [];
  always_known = false;
  upgrade_key = "";
  count_as_class_id = "";

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      if (obj.spells) {
        this.spells = obj.spells;
      } else if (obj.spell_ids) {
        const spell_ids: IStringNumHash = obj.spell_ids;
        for (const spell_id of Object.keys(spell_ids)) {
          this.spells.push({ spell_id, level: spell_ids[spell_id] });
        }
      }
      this.always_known = obj.always_known ? obj.always_known : false;
      this.upgrade_key = obj.upgrade_key ? obj.upgrade_key : "";
      this.count_as_class_id = obj.count_as_class_id ? obj.count_as_class_id : null;
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     if (obj.spells) {
  //       this.spells = obj.spells;
  //     } else if (obj.spell_ids) {
  //       const spell_ids: IStringNumHash = obj.spell_ids;
  //       for (const spell_id of Object.keys(spell_ids)) {
  //         this.spells.push({ spell_id, level: spell_ids[spell_id] });
  //       }
  //     }
  //     this.always_known = obj.always_known ? obj.always_known : false;
  //     this.upgrade_key = obj.upgrade_key ? obj.upgrade_key : "";
  //     this.count_as_class_id = obj.count_as_class_id ? obj.count_as_class_id : null;
  //   }
  // }

  get spell_ids() {
    return this.spells.map(o => o.spell_id);
  }

  spell_level(spell_id: string) {
    const entry = this.spells.find(o => o.spell_id === spell_id);
    if (entry) {
      return entry.level;
    }
    return 1;
  }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      spells: this.spells,
      always_known: this.always_known,
      upgrade_key: this.upgrade_key,
      count_as_class_id: this.count_as_class_id
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof BonusSpells) {
      this.safe_copy(copyMe);
    } else if (copyMe instanceof BonusSpellsUpgrade) {
      this.upgrade_key = copyMe.upgrade_key;
    }
  }

  safe_copy(copyMe: BonusSpells) {
    this.spells = copyMe.spells.map(o => { return {...o}; });
    this.always_known = copyMe.always_known;
    this.upgrade_key = copyMe.upgrade_key;
    this.count_as_class_id = copyMe.count_as_class_id;
  }

  clone(drop_ids = false): BonusSpells {
    const new_obj = new BonusSpells();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

/**
 * This is used to change a Bonus Spells feature from not being always known to being always known
 */
export class BonusSpellsUpgrade extends FloatingFeature {
  upgrade_key = "";
  spellcasting_ability: AbilityScoreExpandedType = "None";
  spellcasting_ability_key = "";

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.upgrade_key = obj.upgrade_key ? obj.upgrade_key : "";
      this.spellcasting_ability = obj.spellcasting_ability ? obj.spellcasting_ability : "None";
      this.spellcasting_ability_key = obj.spellcasting_ability_key ? obj.spellcasting_ability_key : "";
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.upgrade_key = obj.upgrade_key ? obj.upgrade_key : "";
  //     this.spellcasting_ability = obj.spellcasting_ability ? obj.spellcasting_ability : "None";
  //     this.spellcasting_ability_key = obj.spellcasting_ability_key ? obj.spellcasting_ability_key : "";
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      upgrade_key: this.upgrade_key,
      spellcasting_ability: this.spellcasting_ability,
      spellcasting_ability_key: this.spellcasting_ability_key,
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof BonusSpellsUpgrade) {
      this.safe_copy(copyMe);
    } else if (copyMe instanceof BonusSpells) {
      this.upgrade_key = copyMe.upgrade_key;
    }
  }

  safe_copy(copyMe: BonusSpellsUpgrade) {
    this.upgrade_key = copyMe.upgrade_key;
    this.spellcasting_ability = copyMe.spellcasting_ability;
    this.spellcasting_ability_key = copyMe.spellcasting_ability_key;
  }

  clone(drop_ids = false): BonusSpellsUpgrade {
    const new_obj = new BonusSpellsUpgrade();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

/**
 * These are features that let you draw on 
 * extra spell lists when choosing spells.
 * I made it for Divine Soul Sorcerer, 
 * but I expect more will use it too.
 */
export class ExtraSpellLists extends FloatingFeature {
  count_as_class_id = ""; // Class to add them too
  list_ids: string[] = ["Any"]; // Spell List ids
  // Limitations on spells from those ids
  allow_rituals: boolean | null = null; 
  ritual_only = false;
  magic_schools: string[] = ["Any"];
  levels: string[] = ["Any"];
  specific_spell_ids: string[] = [];

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.count_as_class_id = obj.count_as_class_id ? obj.count_as_class_id : "None";
      this.list_ids = obj.list_ids ? obj.list_ids : ["Any"];
      
      this.allow_rituals = obj.allow_rituals !== undefined ? obj.allow_rituals : null;
      this.ritual_only = this.allow_rituals === true && obj.ritual_only ? obj.ritual_only : false;
      this.magic_schools = obj.magic_schools ? obj.magic_schools : ["Any"];
      this.levels = [];
      if (obj.levels) {
        this.levels = obj.levels;
      } else if (obj.max_level !== undefined) {
        const max_level = +obj.max_level;
        if (max_level === 0) {
          this.levels.push("Cantrips");
        } else {
          for (let i = 1; i <= max_level; ++i) {
            this.levels.push(`${i}`);
          }
        }
      } else {
        this.levels.push("Any");
      }
      this.specific_spell_ids = obj.specific_spell_ids ? obj.specific_spell_ids : [];
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.count_as_class_id = obj.count_as_class_id ? obj.count_as_class_id : "None";
  //     this.list_ids = obj.list_ids ? obj.list_ids : ["Any"];
      
  //     this.allow_rituals = obj.allow_rituals !== undefined ? obj.allow_rituals : null;
  //     this.ritual_only = this.allow_rituals === true && obj.ritual_only ? obj.ritual_only : false;
  //     this.magic_schools = obj.magic_schools ? obj.magic_schools : ["Any"];
  //     this.levels = [];
  //     if (obj.levels) {
  //       this.levels = obj.levels;
  //     } else if (obj.max_level !== undefined) {
  //       const max_level = +obj.max_level;
  //       if (max_level === 0) {
  //         this.levels.push("Cantrips");
  //       } else {
  //         for (let i = 1; i <= max_level; ++i) {
  //           this.levels.push(`${i}`);
  //         }
  //       }
  //     } else {
  //       this.levels.push("Any");
  //     }
  //     this.specific_spell_ids = obj.specific_spell_ids ? obj.specific_spell_ids : [];
  //   }
  // }

  get the_levels(): number[] {
    let the_levels: number[] = [];
    if (this.levels.includes("Any")) {
      return [0,1,2,3,4,5,6,7,8,9];
    }
    if (this.levels.includes("Leveled Spells")) {
      the_levels = [1,2,3,4,5,6,7,8,9];
    }
    if (this.levels.includes("Cantrips")) {
      the_levels.push(0);
    }
    if (!this.levels.includes("Leveled Spells")) {
      the_levels = [...the_levels,...this.levels.filter(o => o !== "Cantrips").map(o => +o)];
    }
    return the_levels;
  }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      count_as_class_id: this.count_as_class_id,
      list_ids: this.list_ids,
      allow_rituals: this.allow_rituals,
      ritual_only: this.ritual_only,
      magic_schools: this.magic_schools,
      levels: this.levels,
      specific_spell_ids: this.specific_spell_ids
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof ExtraSpellLists) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: ExtraSpellLists) {
    this.count_as_class_id = copyMe.count_as_class_id;
    this.list_ids = [...copyMe.list_ids];
    this.allow_rituals = copyMe.allow_rituals;
    this.ritual_only = copyMe.ritual_only;
    this.magic_schools = [...copyMe.magic_schools];
    this.specific_spell_ids = [...copyMe.specific_spell_ids];
    this.levels = [...copyMe.levels];
  }

  clone(drop_ids = false): ExtraSpellLists {
    const new_obj = new ExtraSpellLists();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
